import type {
  ActionListItemDescriptor,
  ActionListProps,
  ButtonProps,
  PopoverProps,
} from '@shopify/polaris';

import {
  ChannelsIcon,
  ChartVerticalIcon,
  CodeIcon,
  DeleteIcon,
  DuplicateIcon,
  EditIcon,
  ExchangeIcon,
  FormsIcon,
  LinkIcon,
  MenuHorizontalIcon,
  SettingsIcon,
  ThumbsUpIcon,
  ViewIcon,
  XCircleIcon,
  XIcon,
} from '@shopify/polaris-icons';
import { BlockStack, Icon, ActionList, Popover } from '@shopify/polaris';
import { PreviewMajorIcon } from '~/presentation/icons';
import { useCallback, useMemo, useState } from 'react';
import { GButton } from '~/presentation/ui';
import { GBadge } from '../GBadge';

export interface GActionListItemProp extends ActionListItemDescriptor {
  show?: boolean;
  size?: ButtonProps['size'];
}
type ActionPresetKey =
  | 'EDIT'
  | 'RENAME'
  | 'CHANGE'
  | 'DUPLICATE'
  | 'PREVIEW'
  | 'VIEW_LIVE'
  | 'COPY'
  | 'CODE'
  | 'WINNER'
  | 'ENDED'
  | 'GLOBAL_SETTING'
  | 'DISMISS'
  | 'CHANGE_URL'
  | 'DELETE'
  | 'ANALYTIC';

export interface GActionListProps {
  children?: PopoverProps['activator'];
  buttonProps?: ButtonProps;
  actionList: Readonly<{
    items: GActionListItemProp[];
  }>;
  height?: React.CSSProperties['height'];
  /** Widens the popover content past its hug width when the design asks for it. */
  minWidth?: React.CSSProperties['minWidth'];
  preferredPosition?: PopoverProps['preferredPosition'];
  preferredAlignment?: PopoverProps['preferredAlignment'];
}

export const ACTION_PRESET: Record<ActionPresetKey, GActionListItemProp> = {
  EDIT: {
    id: 'edit',
    content: 'Edit',
    icon: EditIcon,
  },
  CHANGE: {
    id: 'change',
    content: 'Change',
    icon: ExchangeIcon,
  },
  RENAME: {
    id: 'rename',
    content: 'Rename',
    icon: FormsIcon,
  },
  DUPLICATE: {
    id: 'duplicate',
    content: 'Duplicate',
    icon: DuplicateIcon,
  },
  PREVIEW: {
    id: 'preview',
    content: 'Preview',
    icon: ViewIcon,
  },
  VIEW_LIVE: {
    id: 'view_live',
    content: 'View live',
    icon: PreviewMajorIcon,
  },
  COPY: {
    id: 'copy',
    content: 'Copy live page link',
    icon: LinkIcon,
  },
  CODE: {
    id: 'code',
    content: 'Code',
    prefix: (
      <BlockStack align={'center'}>
        <Icon source={CodeIcon}></Icon>
      </BlockStack>
    ),
  },
  WINNER: {
    id: 'winner',
    content: 'Choose winner',
    icon: ThumbsUpIcon,
    suffix: <GBadge tone="info">Beta</GBadge>,
  },
  CHANGE_URL: {
    id: 'change_url',
    content: 'Change URL',
    icon: ChannelsIcon,
    suffix: <GBadge tone="info">Beta</GBadge>,
  },
  ENDED: {
    id: 'ended',
    content: 'End experiment',
    icon: XCircleIcon,
  },
  GLOBAL_SETTING: {
    id: 'global_setting',
    content: 'Global settings',
    icon: SettingsIcon,
  },
  DELETE: {
    id: 'delete',
    content: 'Delete',
    icon: DeleteIcon,
    destructive: true,
  },
  DISMISS: {
    id: 'dismiss',
    content: 'Dismiss',
    icon: XIcon,
  },
  ANALYTIC: {
    id: 'analytic',
    content: 'View analytic',
    icon: ChartVerticalIcon,
  },
};
export const GActionList: React.FC<GActionListProps> = ({
  buttonProps,
  actionList,
  height = '28px',
  minWidth,
  preferredPosition,
  preferredAlignment,
}) => {
  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const activator = useMemo(() => {
    return (
      <GButton
        fullWidth
        icon={MenuHorizontalIcon}
        {...buttonProps}
        {...(buttonProps?.disclosure && { disclosure: active ? 'up' : 'down' })}
        onClick={toggleActive}
      ></GButton>
    );
  }, [active, buttonProps, toggleActive]);

  const actionListPreset: ActionListProps['items'] = Object.values(ACTION_PRESET);

  const combineActionList = actionList.items.reduce<GActionListItemProp[]>((acc, item) => {
    const actionItem = actionListPreset.find((action) => action.id === item.id);
    if (actionItem) {
      acc.push({
        ...actionItem,
        ...item,
        onAction: async () => {
          await item.onAction?.();
          toggleActive();
        },
      });
    }
    return acc;
  }, []);

  return (
    <div style={{ display: 'flex', height }}>
      <Popover
        // activatorWrapper="tooltip-content"
        fullHeight
        active={active}
        activator={activator}
        fluidContent
        autofocusTarget="container"
        preferredPosition={preferredPosition}
        preferredAlignment={preferredAlignment}
        onClose={toggleActive}
      >
        <div
          className={'Polaris-GActionList'}
          style={{ minWidth }}
          onClick={(e) => e && e.stopPropagation()}
        >
          <ActionList items={combineActionList} />
        </div>
      </Popover>
    </div>
  );
};
